import { FormikValues } from "formik";
import PreconfirmationStep from "../../components/PreconfirmationCard/PreconfirmationStep/PreconfirmationStep";
import { investmentTools } from "../../i18n/securities/options";
import { stepNames } from "../../i18n/securities/preconfirmationStep";
import { texts as stepFourTexts } from "../../i18n/securities/stepFour";
import {
  BasicStep,
  Field,
  Option,
  PreconfirmationField,
  SecuritiesFormikValues,
} from "../../types";
import StepFive from "./stepFive";
import StepFour from "./stepFour";
import StepOne from "./stepOne";
import StepThree from "./stepThree";
import StepTwo from "./stepTwo";
import { AppDispatch, RootState } from "../../app/store";
import { HolderData } from "../../reducers/securitiesReducer";

const stepFieldsFormatted = ({
  fields,
  values,
  dispatch,
  state,
}: {
  fields: Field[];
  values: FormikValues;
  dispatch: AppDispatch;
  state: RootState;
}) => {
  return fields.map(
    ({
      name,
      label,
      type,
      options,
      conditional,
      preconfirmationLabel,
      conditionalBool,
      formatInPreConfirmation,
    }) => ({
      name,
      label: preconfirmationLabel ?? label,
      type,
      options,
      isConditional:
        !!conditional ||
        !!(conditionalBool ? !conditionalBool(values, dispatch, state) : false),
      formatInPreConfirmation,
    })
  );
};

// STEP 1 - Personal Information
const stepOnePreconfirmation = ({
  values,
  dispatch,
  state,
  countriesOptions,
  currentHolder,
  holdersData,
}: {
  values: FormikValues;
  dispatch: AppDispatch;
  state: RootState;
  countriesOptions: Option[];
  currentHolder: number;
  holdersData: HolderData[];
}): PreconfirmationStep => {
  const { language } = state.securities;

  const stepOneFields =
    StepOne({ language, countriesOptions, currentHolder, holdersData })
      .fields ?? [];

  const stepOneFieldsFormatted = stepFieldsFormatted({
    fields: stepOneFields,
    values,
    dispatch,
    state,
  });

  const stepOneFieldsFormattedKeys: {
    [key: string]: PreconfirmationField;
  } = stepOneFieldsFormatted.reduce((acc, field) => {
    return { ...acc, [field.name]: field };
  }, {});

  const fieldsToInclude: (keyof SecuritiesFormikValues["stepOne"])[] = [
    "name",
    "lastName",
    "motherName",
    "gender",
    "countryOfBirth",
    "birthDate",
    "civilStatus",
    "educationLevel",
    "dependants",
    "mobilePhoneAR",
    "mobilePhoneUS",
    "homePhoneAR",
    "homePhoneUS",
    "workPhoneAR",
    "workPhoneUS",
  ];

  const addressFields = [
    "taxAddresses.fullAddress",
    "taxAddresses.city",
    "taxAddresses.state",
    "taxAddresses.country",
    "postalAddresses.fullAddress",
    "postalAddresses.city",
    "postalAddresses.state",
    "postalAddresses.country",
  ];

  return {
    name: stepNames[language].personalInformation,
    columns: 4,
    fields: [
      ...fieldsToInclude.map((field) => stepOneFieldsFormattedKeys[field]),
      ...addressFields.map((field) => stepOneFieldsFormattedKeys[field]),
    ],
    editStep: {
      step: 1,
    },
  };
};

// STEP 2 - Identification
const stepTwoPreconfirmation = ({
  values,
  dispatch,
  state,
  countriesOptions,
  citizenshipOptions,
  currentHolder,
}: {
  values: FormikValues;
  dispatch: AppDispatch;
  state: RootState;
  countriesOptions: Option[];
  citizenshipOptions: Option[];
  currentHolder: number;
}): PreconfirmationStep => {
  const { language } = state.securities;

  const stepTwoFields =
    StepTwo({ countriesOptions, citizenshipOptions, language, currentHolder })
      .fields ?? [];

  const stepTwoFieldsFormatted = stepFieldsFormatted({
    fields: stepTwoFields,
    values,
    dispatch,
    state,
  });

  const stepTwoFieldsFormattedKeys: {
    [key: string]: PreconfirmationField;
  } = stepTwoFieldsFormatted.reduce((acc, field) => {
    return { ...acc, [field.name]: field };
  }, {});

  const fieldsToInclude: (keyof SecuritiesFormikValues["stepTwo"])[] = [
    "USResident",
    "residenceCountry",
    "firstCitizenship",
    "additionalCitizenshipBool",
    "additionalCitizenship",
    "socialSecurityNumber",
    "documentTypeAR",
    "documentTypeUS",
    "documentNumber",
    "passportNumber",
    "documentCountry",
    "documentEmisionDate",
    "documentExpirationDate",
    "taxId",
  ];

  return {
    name: stepNames[language].identification,
    columns: 4,
    fields: [
      ...fieldsToInclude.map((field) => stepTwoFieldsFormattedKeys[field]),
    ],
    editStep: {
      step: 2,
    },
  };
};

// STEP 3 - Employment and Financial Information
const stepThreePreconfirmation = ({
  values,
  dispatch,
  state,
  countriesOptions,
  currentHolder,
}: {
  values: FormikValues;
  dispatch: AppDispatch;
  state: RootState;
  countriesOptions: Option[];
  currentHolder: number;
}): PreconfirmationStep => {
  const { language } = state.securities;

  const stepThreeFields =
    StepThree({ countriesOptions, language, currentHolder }).fields ?? [];

  const stepThreeFieldsFormatted = stepFieldsFormatted({
    fields: stepThreeFields,
    values,
    dispatch,
    state,
  });

  const stepThreeFieldsFormattedKeys: {
    [key: string]: PreconfirmationField;
  } = stepThreeFieldsFormatted.reduce((acc, field) => {
    return { ...acc, [field.name]: field };
  }, {});

  const fieldsToInclude = [
    "employmentStatus",
    "occupation",
    "yearsWorked",
    "companyType",
    "previousOccupation",
    "yearsPreviouslyWorked",
    "companyName",
    "companyCountry",
    "companyAddresses.fullAddress",
    "companyAddresses.city",
    "companyAddresses.state",
    "annualIncomeLabel",
    "netIncomeLabel",
    "netWorthLabel",
  ];

  return {
    name: stepNames[language].employmentAndFinancialInformation,
    columns: 4,
    fields: [
      ...fieldsToInclude.map((field) => stepThreeFieldsFormattedKeys[field]),
    ],
    editStep: {
      step: 3,
    },
  };
};

// STEP 4 - Investment Experience
const stepFourPreconfirmation = ({
  values,
  dispatch,
  state,
  currentHolder,
}: {
  values: FormikValues;
  dispatch: AppDispatch;
  state: RootState;
  currentHolder: number;
}): PreconfirmationStep => {
  const { language } = state.securities;

  const stepFourFields = StepFour({ language, currentHolder }).fields ?? [];

  const stepFourFieldsFormatted = stepFieldsFormatted({
    fields: stepFourFields,
    values,
    dispatch,
    state,
  });

  const stepFourFieldsFormattedKeys: {
    [key: string]: PreconfirmationField;
  } = stepFourFieldsFormatted.reduce((acc, field) => {
    return { ...acc, [field.name]: field };
  }, {});

  const fieldsToInclude: (keyof SecuritiesFormikValues["stepFour"])[] = [
    "hasOtherInvestmentAccountsBool",
    "otherInvestmentAccountName",
    "otherInvestmentAccountYears",
    "isAffiliatedWithBroker",
    "brokerName",
    "brokerEmployeeName",
    "brokerEmployeeRelationship",
  ];

  const investmentToolsFieldsNames = investmentTools[language].map(
    (investmentTool) => {
      return investmentTool.name;
    }
  );

  // parse investment tools fields to a single field
  const investmentToolField = (): PreconfirmationField => {
    return {
      name: "investmentTools",
      label: stepFourTexts[language].section1,
      type: "label",
      formatInPreConfirmation: (_, formValues) => {
        // return all investment tools fields in a single string, use investmentToolsFields
        const values = formValues as SecuritiesFormikValues["stepFour"];
        if (!values || values.investmentsExperience === "NONE") return "";
        return investmentToolsFieldsNames
          .map((name) => {
            return values.investmentTools[name]?.bool === true
              ? stepFourFieldsFormattedKeys[`investmentTools.${name}.bool`]
                  .label
              : "";
          })
          .filter((value) => value)
          .join(", ")
          .trim();
      },
      isConditional:
        stepFourFieldsFormattedKeys["hasOtherInvestmentAccountsBool"]
          .isConditional,
    };
  };

  return {
    name: stepNames[language].investmentExperience,
    columns: 3,
    fields: [
      stepFourFieldsFormattedKeys["investmentsExperience"],
      investmentToolField(),
      ...fieldsToInclude.map((field) => stepFourFieldsFormattedKeys[field]),
    ],
    editStep: {
      step: 4,
    },
  };
};

// STEP 5 - Planning and Use of the Account and Designation of Beneficiaries
const stepFivePreconfirmation = ({
  values,
  dispatch,
  state,
  countriesOptions,
}: {
  values: FormikValues;
  dispatch: AppDispatch;
  state: RootState;
  countriesOptions: Option[];
}): PreconfirmationStep[] => {
  const { language, accountBeneficiaries, beneficiariesCount } =
    state.securities;

  const subStepOne = StepFive({
    language,
    countriesOptions,
    beneficiariesCount,
  }).subSteps?.[0] as BasicStep;
  const subStepTwo = StepFive({
    language,
    countriesOptions,
    beneficiariesCount,
  }).subSteps?.[1] as BasicStep;
  const subStepOneFields = subStepOne?.fields ?? [];
  const subStepTwoFields = subStepTwo?.fields ?? [];

  const subStepOneFieldsFormatted = stepFieldsFormatted({
    fields: subStepOneFields,
    values,
    dispatch,
    state,
  });

  const subStepTwoFieldsFormatted = stepFieldsFormatted({
    fields: subStepTwoFields,
    values,
    dispatch,
    state,
  });

  const subStepOneFieldsFormattedKeys: {
    [key: string]: PreconfirmationField;
  } = subStepOneFieldsFormatted.reduce((acc, field) => {
    return { ...acc, [field.name]: field };
  }, {});

  const subStepTwoFieldsFormattedKeys: {
    [key: string]: PreconfirmationField;
  } = subStepTwoFieldsFormatted.reduce((acc, field) => {
    return { ...acc, [field.name]: field };
  }, {});

  const subStepOnefieldsToInclude = [
    "originOfFunds",
    "otherOriginOfFunds",
    "fundingDate",
    "initialTransferAmount",
    "fundingSource",
    "timeHorizon",
    "riskProfile",
    "regularWithdrawals.bool",
    "regularWithdrawals.reason",
    "regularWithdrawals.otherReason",
    "regularWithdrawals.frequency",
    "regularWithdrawals.otherFrequency",
    "thirdPartyTransfers.bool",
    "thirdPartyTransfers.frequency",
    "thirdPartyTransfers.otherFrequency",
    "thirdPartyTransfers.reason",
    "thirdPartyTransfers.otherReason",
    "tradeOptions.bool",
    "tradeOptions.options",
    "tradeMargins.bool",
  ];

  const subStepTwofieldsToInclude = (index: number) => [
    `beneficiary${index}.name`,
    `beneficiary${index}Percentage`,
    `beneficiary${index}.gender`,
    `beneficiary${index}.birthDate`,
    `beneficiary${index}.phoneAR`,
    `beneficiary${index}.phoneUS`,
    `beneficiary${index}.email`,
    `beneficiary${index}.taxIdType`,
    `beneficiary${index}.taxIdNumber`,
    `beneficiary${index}.socialSecurityNumber`,
    `beneficiary${index}.relationship`,
    `beneficiary${index}.country`,
    `beneficiary${index}.fullAddress`,
    `beneficiary${index}.city`,
    `beneficiary${index}.zipCode`,
    `beneficiary${index}.state`,
  ];

  return [
    {
      name: stepNames[language].accountObjective,
      columns: 3,
      fields: [
        subStepOneFieldsFormattedKeys["accountObjective"],
        ...subStepOnefieldsToInclude.map(
          (field) => subStepOneFieldsFormattedKeys[field]
        ),
      ],
      editStep: {
        step: 5,
        substep: 1,
      },
    },
    ...(accountBeneficiaries
      ? [
          {
            name: stepNames[language].beneficiaries,
            columns: 3,
            fields: [],
            fieldsWithSeparator: Array.from(
              { length: beneficiariesCount },
              (_, i) => i
            ).map((i) =>
              subStepTwofieldsToInclude(i + 1).map(
                (field) => subStepTwoFieldsFormattedKeys[field]
              )
            ),
            editStep: {
              step: 5,
              substep: 2,
            },
          },
        ]
      : []),
  ];
};

// Preconfirmation Steps
export const preconfirmationSteps = ({
  values,
  dispatch,
  state,
  countriesOptions,
  citizenshipOptions,
  currentHolder,
  holdersData,
}: {
  values: FormikValues;
  dispatch: AppDispatch;
  state: RootState;
  countriesOptions: Option[];
  citizenshipOptions: Option[];
  currentHolder: number;
  holdersData: HolderData[];
}): PreconfirmationStep[] => {
  return [
    stepOnePreconfirmation({
      values,
      dispatch,
      state,
      countriesOptions,
      currentHolder,
      holdersData,
    }),
    stepTwoPreconfirmation({
      values,
      dispatch,
      state,
      countriesOptions,
      citizenshipOptions,
      currentHolder,
    }),
    stepThreePreconfirmation({
      values,
      dispatch,
      state,
      countriesOptions,
      currentHolder,
    }),
    stepFourPreconfirmation({ values, dispatch, state, currentHolder }),
    ...(currentHolder === 1
      ? [
          ...stepFivePreconfirmation({
            values,
            dispatch,
            state,
            countriesOptions,
          }),
        ]
      : []),
  ];
};
